import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import OnboardClientDialog from "@/components/OnboardClientDialog";
import { ActivationBadge, PipelineFilter, applyPipelineFilter, pipelineCounts } from "@/components/ActivationBadge";
import { toast } from "sonner";
import { Loader2, Briefcase, ArrowRight, FolderOpen, Users } from "lucide-react";

const STATUS_COLORS = {
  Onboarding: "bg-[#EDE9FE] text-[#5B21B6]",
  Vault: "bg-[#CFFAFE] text-[#155E75]",
  Intake: "bg-[#E6F0EB] text-[#114A31]",
  Review: "bg-[#FEF3C7] text-[#92600A]",
  Filed: "bg-[#DBEAFE] text-[#1E40AF]",
  Complete: "bg-[#DCFCE7] text-[#166534]",
};

export default function TechDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipelineFilter, setPipelineFilter] = useState("all");
  const visibleCases = applyPipelineFilter(cases, pipelineFilter);
  const onInviteResent = (caseId) =>
    setCases((cs) => cs.map((c) => c.case_id === caseId
      ? { ...c, last_invite_at: new Date().toISOString(), invite_count: (c.invite_count || 1) + 1 } : c));

  useEffect(() => {
    if (user && user.role === "tech") {
      api.get("/staff/cases").then(({ data }) => setCases(data)).catch(() => toast.error("Could not load your cases")).finally(() => setLoading(false));
    }
  }, [user]);

  if (user && user.role !== "tech") return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;

  const active = cases.filter((c) => c.status !== "Complete").length;

  const reloadCases = () => api.get("/staff/cases").then(({ data }) => setCases(data)).catch(() => {});

  return (
    <div className="min-h-screen bg-[#F9F9F8] font-body">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 sm:px-12 pt-28 pb-20">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#114A31] flex items-center gap-2">
          <Briefcase className="h-3.5 w-3.5" /> Specialist Workspace
        </span>
        <h1 className="mt-3 font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]">
          Welcome, {user?.name?.split(" ")[0] || "Specialist"}
        </h1>
        <p className="mt-2 text-[#595D5A]">Clients assigned to you. Open a case to request docs, deliver files, and message the client.</p>

        <div className="mt-5">
          <OnboardClientDialog onCreated={reloadCases} />
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
            <Users className="h-5 w-5 text-[#114A31]" />
            <p className="mt-3 font-heading text-3xl font-bold text-[#1C1C1C]">{cases.length}</p>
            <p className="text-sm text-[#595D5A]">Assigned clients</p>
          </div>
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
            <FolderOpen className="h-5 w-5 text-[#114A31]" />
            <p className="mt-3 font-heading text-3xl font-bold text-[#1C1C1C]">{active}</p>
            <p className="text-sm text-[#595D5A]">Active cases</p>
          </div>
        </div>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#114A31]" /></div>
        ) : cases.length === 0 ? (
          <div className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center" data-testid="tech-empty">
            <Briefcase className="h-10 w-10 text-[#595D5A] mx-auto" />
            <p className="mt-3 font-medium text-[#1C1C1C]">No clients assigned yet</p>
            <p className="text-sm text-[#595D5A]">Your admin will assign clients to you here.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-3" data-testid="tech-cases">
            <PipelineFilter value={pipelineFilter} onChange={setPipelineFilter} counts={pipelineCounts(cases)} />
            {visibleCases.length === 0 && (
              <p className="text-sm text-[#595D5A] bg-white border border-[#E5E5E5] rounded-2xl p-6 text-center" data-testid="pipeline-filter-empty">
                No cases match this filter.
              </p>
            )}
            {visibleCases.map((c) => (
              <div key={c.case_id} data-testid={`tech-case-${c.case_id}`}
                className="bg-white border border-[#E5E5E5] rounded-2xl p-5 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-heading font-semibold text-[#1C1C1C]">{c.business_name}</h3>
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[c.status] || ""}`}>{c.status}</span>
                    <ActivationBadge c={c} onResent={onInviteResent} />
                  </div>
                  <p className="text-sm text-[#595D5A] mt-1 truncate">{c.user_name} · {c.user_email} · {c.files?.length || 0} doc(s)</p>
                </div>
                <Button onClick={() => navigate(`/staff/case/${c.case_id}`)} data-testid={`tech-open-${c.case_id}`}
                  className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white shrink-0">Open <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
